import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import Draggable from 'react-draggable';
import type { DraggableData, DraggableEvent } from 'react-draggable';
import {
  MdCenterFocusStrong,
  MdClose,
  MdDownload,
  MdHelpOutline,
  MdRemove,
} from 'react-icons/md';
import { TbArrowsMaximize } from 'react-icons/tb';
import { FiLink, FiLink2 } from 'react-icons/fi';
import { IoChevronBack, IoChevronForward, IoPlaySkipBack, IoPlaySkipForward } from 'react-icons/io5';
import { useImageViewers } from './ImageViewerContext';
import StaticImageViewer, { StaticViewportState } from './StaticImageViewer';
import PanoramicImageViewer from './PanoramicImageViewer';
import { useImages, isBlobImage } from 'customHooks/useImages';
import type { ImageViewerInstance, SyncGroup } from './types';
import { surveyImageViewerGuideUrl } from 'constants/guideRoutes';
import {
  hasSpatialFocusValue,
  NO_SPATIAL_INDEX_MESSAGE,
} from 'utils/syncIndex';
import './style.scss';

const MIN_WIDTH = 350;
const MIN_HEIGHT = 300;

interface ModalPosition {
  readonly x: number;
  readonly y: number;
}

interface PendingAutoSize {
  readonly naturalWidth: number;
  readonly naturalHeight: number;
}

interface Props {
  viewer: ImageViewerInstance;
  layout?: 'window' | 'slot';
}

const ImageViewerModal: React.FC<Props> = ({ viewer, layout = 'window' }) => {
  const isSlot = layout === 'slot';
  const {
    viewers,
    syncGroups,
    closeViewer,
    setActiveViewer,
    navigateViewer,
    toggleSync,
    setSyncFocusMode,
    updatePanoramaState,
    setViewerLoading,
    setViewerError,
    setViewerMinimized,
    setViewerPosition,
    getSyncGroupColor,
  } = useImageViewers();

  const modalRef = useRef<HTMLDivElement>(null);
  const sizeRef = useRef({ width: 480, height: 450 });
  const [size, setSize] = useState({ width: 480, height: 450 });
  const hasResized = useRef(false);
  const autoSizedForPathRef = useRef<string | null>(null);
  const pendingAutoSizeRef = useRef<PendingAutoSize | null>(null);
  const isDraggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState<ModalPosition>(viewer.position);
  const [navbarWidth] = useState<number>(
    (document.querySelector('.xa-navbar.collapsed') as HTMLElement)?.offsetWidth ?? 48,
  );
  const [staticViewportState, setStaticViewportState] = useState<StaticViewportState | null>(null);
  const [isImageReady, setIsImageReady] = useState(false);

  // Keep local drag position aligned with context when not mid-drag (e.g. N/W resize).
  useEffect(() => {
    if (isDraggingRef.current) return;
    setDragPosition(viewer.position);
  }, [viewer.position]);

  // Can sync if there are other viewers for the same survey
  const canSync = viewers.some(
    (v) => v.surveySlug === viewer.surveySlug && v.id !== viewer.id,
  );

  // Sync group info
  const syncGroup: SyncGroup | undefined = viewer.syncGroupId
    ? syncGroups.find((g) => g.id === viewer.syncGroupId)
    : undefined;
  const syncColor = getSyncGroupColor(viewer.syncGroupId);
  const currentFeature = viewer.layerFeatures[viewer.sequentialIndex];
  const featureHasSpatialIndex = hasSpatialFocusValue(currentFeature);
  const canShowFocusToggle = Boolean(viewer.isSynced && syncGroup);
  const isSpatialSyncMode = syncGroup?.syncMode === 'spatial';
  const isFocusOwner = Boolean(isSpatialSyncMode && syncGroup?.focusViewerId === viewer.id);
  const focusToggleDisabled = !isSpatialSyncMode && !featureHasSpatialIndex;
  const focusTooltip = focusToggleDisabled
    ? NO_SPATIAL_INDEX_MESSAGE
    : isSpatialSyncMode
      ? 'Remove focus'
      : 'Set focus';

  const hasPrevious = viewer.sequentialIndex > 0;
  const hasNext = viewer.sequentialIndex < viewer.layerFeatures.length - 1;
  const shouldFlipStaticImage = /\b(back|rear)\b/i.test(
    `${viewer.layerName ?? ''} ${viewer.layerKey ?? ''}`,
  );
  const preloadUrls = useMemo(() => {
    const orderedUrls: string[] = [];
    const seen = new Set<string>();

    const pushIndex = (index: number) => {
      if (index < 0 || index >= viewer.layerFeatures.length) return;
      const filePath = viewer.layerFeatures[index]?.filePath;
      if (!filePath || seen.has(filePath)) return;
      seen.add(filePath);
      orderedUrls.push(filePath);
    };

    if (viewer.currentFilePath) {
      seen.add(viewer.currentFilePath);
      orderedUrls.push(viewer.currentFilePath);
    }

    pushIndex(viewer.sequentialIndex + 1);
    pushIndex(viewer.sequentialIndex - 1);
    pushIndex(viewer.sequentialIndex + 2);
    pushIndex(viewer.sequentialIndex - 2);

    return orderedUrls;
  }, [viewer.currentFilePath, viewer.layerFeatures, viewer.sequentialIndex]);

  const { images, isLoading: isBlobLoading, error: blobRequestError } = useImages(
    preloadUrls,
  );

  const activeResult = viewer.currentFilePath
    ? images.find((image) => image.url === viewer.currentFilePath)
    : undefined;
  const activeBlobImage = activeResult && isBlobImage(activeResult) ? activeResult : null;
  const activeBlobImageError =
    !isBlobLoading && activeResult && !isBlobImage(activeResult) ? activeResult.error : null;
  const activeImageSrc = activeBlobImage?.dataUri ?? null;
  const activeErrorMessage = activeBlobImageError || blobRequestError;
  const isActiveImagePending =
    !!viewer.currentFilePath && isBlobLoading && !activeBlobImage && !activeBlobImageError;
  const downloadFileName = useMemo(() => {
    const fallbackExt = viewer.imageType === 'PANO' ? '.jpg' : '.jpg';
    const sourcePath = viewer.currentFilePath || viewer.layerFeatures[viewer.sequentialIndex]?.filePath || '';
    const withoutQuery = sourcePath.split('?')[0];
    const candidate = withoutQuery.split('/').pop() || 'image';
    const decoded = decodeURIComponent(candidate);
    return /\.[a-zA-Z0-9]+$/.test(decoded) ? decoded : `${decoded}${fallbackExt}`;
  }, [viewer.currentFilePath, viewer.imageType, viewer.layerFeatures, viewer.sequentialIndex]);

  const handleClose = useCallback(() => closeViewer(viewer.id), [closeViewer, viewer.id]);
  const handleOpenGuide = useCallback(() => {
    window.open(surveyImageViewerGuideUrl(), '_blank', 'noopener,noreferrer');
  }, []);
  const handleActivate = useCallback(
    (options?: { autoPan?: boolean; raiseToFront?: boolean }) =>
      setActiveViewer(viewer.id, options),
    [setActiveViewer, viewer.id],
  );

  // Stable ref so the mousedown handler sees the latest isActive value without
  // needing to re-register the listener on every render.
  const isActiveRef = useRef(viewer.isActive);
  isActiveRef.current = viewer.isActive;

  // Activate (bring to top) when clicking anywhere in the modal including the header.
  // We use a native capture-phase listener because react-draggable overrides the
  // child's onMouseDown via cloneElement.
  // Exception: clicking the minimize/maximize or close buttons on a non-active
  // viewer should not make it the active viewer.
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Element;
      const inHeaderButtons = !!target.closest('.modal-header-buttons');
      const inHeader = !!target.closest('.modal-header');
      const inBody = !!target.closest('.modal-content') || !!target.closest('.modal-footer');
      if (inHeaderButtons && !isActiveRef.current) return;
      const autoPan = !isActiveRef.current;
      if (inHeader) {
        handleActivate({ autoPan: false });
        return;
      }
      if (inBody) {
        handleActivate({ autoPan, raiseToFront: true });
        return;
      }
      handleActivate({ autoPan, raiseToFront: true });
    };
    el.addEventListener('mousedown', onMouseDown, true);
    return () => el.removeEventListener('mousedown', onMouseDown, true);
  }, [handleActivate]);
  const handleMinimize = useCallback(
    () => setViewerMinimized(viewer.id, !viewer.isMinimized),
    [setViewerMinimized, viewer.id, viewer.isMinimized],
  );
  const handlePrevious = useCallback(
    () => navigateViewer(viewer.id, 'previous'),
    [navigateViewer, viewer.id],
  );
  const handleNext = useCallback(
    () => navigateViewer(viewer.id, 'next'),
    [navigateViewer, viewer.id],
  );
  const handleFirst = useCallback(
    () => navigateViewer(viewer.id, 'first'),
    [navigateViewer, viewer.id],
  );
  const handleLast = useCallback(
    () => navigateViewer(viewer.id, 'last'),
    [navigateViewer, viewer.id],
  );
  const handleToggleSync = useCallback(
    () => toggleSync(viewer.id),
    [toggleSync, viewer.id],
  );
  const handleToggleSyncFocus = useCallback(
    () => setSyncFocusMode(viewer.id, !isSpatialSyncMode),
    [isSpatialSyncMode, setSyncFocusMode, viewer.id],
  );
  const handleDownload = useCallback(() => {
    if (!activeImageSrc) return;

    const anchor = document.createElement('a');
    anchor.href = activeImageSrc;
    anchor.download = downloadFileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }, [activeImageSrc, downloadFileName]);

  const applyAutoSizeToImageAspect = useCallback(
    (naturalWidth: number, naturalHeight: number) => {
      if (!naturalWidth || !naturalHeight) return;
      if (hasResized.current) return;
      if (autoSizedForPathRef.current === viewer.currentFilePath) return;

      const el = modalRef.current;
      if (!el || viewer.isMinimized) return;

      const header = el.querySelector('.modal-header') as HTMLElement | null;
      const footer = el.querySelector('.modal-footer') as HTMLElement | null;
      const headerH = header?.getBoundingClientRect().height ?? 0;
      const footerH = footer?.getBoundingClientRect().height ?? 0;
      const chromeH = headerH + footerH;

      const aspect = naturalWidth / naturalHeight;
      const rootFontPx =
        parseFloat(getComputedStyle(document.documentElement).fontSize || '16') || 16;
      const maxW = Math.max(MIN_WIDTH, window.innerWidth - 11.2 * rootFontPx);
      const maxH = Math.max(MIN_HEIGHT, window.innerHeight - 1.6 * rootFontPx);

      const currentRect = el.getBoundingClientRect();
      let desiredW = Math.min(currentRect.width || sizeRef.current.width, maxW);
      let desiredH = chromeH + desiredW / aspect;

      // If too tall, constrain height then recompute width from aspect
      if (desiredH > maxH) {
        desiredH = maxH;
        desiredW = Math.max(MIN_WIDTH, (desiredH - chromeH) * aspect);
      }

      desiredW = Math.min(Math.max(desiredW, MIN_WIDTH), maxW);
      desiredH = Math.min(Math.max(desiredH, MIN_HEIGHT), maxH);

      sizeRef.current = { width: desiredW, height: desiredH };
      hasResized.current = true;
      setSize({ width: desiredW, height: desiredH });
      autoSizedForPathRef.current = viewer.currentFilePath;
    },
    [viewer.currentFilePath, viewer.isMinimized],
  );

  const flushPendingAutoSize = useCallback(() => {
    const pending = pendingAutoSizeRef.current;
    if (!pending) return;
    pendingAutoSizeRef.current = null;
    applyAutoSizeToImageAspect(pending.naturalWidth, pending.naturalHeight);
  }, [applyAutoSizeToImageAspect]);

  const handleDragStart = useCallback(() => {
    isDraggingRef.current = true;
    setIsDragging(true);
  }, []);

  const handleDragStop = useCallback(
    (_e: DraggableEvent, data: DraggableData) => {
      const next: ModalPosition = { x: data.x, y: data.y };
      isDraggingRef.current = false;
      setIsDragging(false);
      setDragPosition(next);
      setViewerPosition(viewer.id, next);
      // Apply image autosize only after drag ends so size changes cannot desync the cursor.
      flushPendingAutoSize();
    },
    [flushPendingAutoSize, setViewerPosition, viewer.id],
  );

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent, direction: string) => {
      e.preventDefault();
      e.stopPropagation();
      handleActivate();

      const startX = e.clientX;
      const startY = e.clientY;
      const el = modalRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const startWidth = rect.width;
      const startHeight = rect.height;
      sizeRef.current = { width: startWidth, height: startHeight };
      const startPosX = dragPosition.x;
      const startPosY = dragPosition.y;

      let finalX = startPosX;
      let finalY = startPosY;

      const onMouseMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;

        let w = startWidth;
        let h = startHeight;
        let x = startPosX;
        let y = startPosY;

        if (direction.includes('e')) w = Math.max(MIN_WIDTH, startWidth + dx);
        if (direction.includes('s')) h = Math.max(MIN_HEIGHT, startHeight + dy);
        if (direction.includes('w')) {
          w = Math.max(MIN_WIDTH, startWidth - dx);
          x = startPosX + (startWidth - w);
        }
        if (direction.includes('n')) {
          h = Math.max(MIN_HEIGHT, startHeight - dy);
          y = startPosY + (startHeight - h);
        }

        sizeRef.current = { width: w, height: h };
        finalX = x;
        finalY = y;

        el.style.width = `${w}px`;
        el.style.height = `${h}px`;
        if (direction.includes('w') || direction.includes('n')) {
          el.style.transform = `translate(${x}px, ${y}px)`;
        }
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        hasResized.current = true;
        setSize({ ...sizeRef.current });
        if (direction.includes('w') || direction.includes('n')) {
          const next: ModalPosition = { x: finalX, y: finalY };
          setDragPosition(next);
          setViewerPosition(viewer.id, next);
        }
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [dragPosition.x, dragPosition.y, handleActivate, viewer.id, setViewerPosition],
  );

  const handleImageLoad = useCallback(() => {
    setIsImageReady(true);
    setViewerError(viewer.id, false);
  }, [setViewerError, viewer.id]);

  // Allow one autosize per image (and only if the user hasn't manually resized)
  useEffect(() => {
    autoSizedForPathRef.current = null;
    pendingAutoSizeRef.current = null;
  }, [viewer.currentFilePath]);

  // Reset image-ready flag whenever the source changes
  useEffect(() => {
    setIsImageReady(false);
  }, [viewer.currentFilePath]);

  const autoSizeToImageAspect = useCallback(
    (naturalWidth: number, naturalHeight: number) => {
      // Defer size changes while the user is dragging so layout updates cannot
      // fight react-draggable's transform tracking.
      if (isDraggingRef.current) {
        pendingAutoSizeRef.current = { naturalWidth, naturalHeight };
        return;
      }
      applyAutoSizeToImageAspect(naturalWidth, naturalHeight);
    },
    [applyAutoSizeToImageAspect],
  );

  const handleStaticImageLoad = useCallback(
    (info: { naturalWidth: number; naturalHeight: number }) => {
      handleImageLoad();
      autoSizeToImageAspect(info.naturalWidth, info.naturalHeight);
    },
    [autoSizeToImageAspect, handleImageLoad],
  );

  const handleImageError = useCallback(() => {
    setViewerError(viewer.id, true);
  }, [setViewerError, viewer.id]);

  // Sync blob-fetch errors into viewer context error state.
  // Loading/spinner state is driven locally by isImageReady to avoid
  // context round-trip flicker.
  useEffect(() => {
    if (activeErrorMessage) {
      setViewerError(viewer.id, true);
    } else if (viewer.hasError && !viewer.noImageAvailable) {
      setViewerError(viewer.id, false);
    }
  }, [activeErrorMessage, setViewerError, viewer.hasError, viewer.id, viewer.noImageAvailable]);

  const handlePanoramaPositionChange = useCallback(
    (yaw: number, pitch: number) => {
      updatePanoramaState(viewer.id, { yaw, pitch });
    },
    [updatePanoramaState, viewer.id],
  );

  const handlePanoramaZoomChange = useCallback(
    (zoom: number) => {
      updatePanoramaState(viewer.id, { zoom });
    },
    [updatePanoramaState, viewer.id],
  );

  // Build outline style for synced modals
  const outlineStyle: React.CSSProperties = {};
  if (viewer.isSynced && syncColor) {
    outlineStyle.outline = `3px solid ${syncColor}`;
    outlineStyle.outlineOffset = '-3px';
  }

  // Active border
  const activeClass = viewer.isActive ? ' image-viewer-modal--active' : '';
  const minimizedClass = !isSlot && viewer.isMinimized ? ' minimized' : '';
  const slotClass = isSlot ? ' image-viewer-modal--slot' : '';

  // Keep at least a grabable strip of the modal in view, even for large autosized windows.
  const dragBounds = useMemo(() => {
    const minVisibleX = Math.min(200, Math.max(80, sizeRef.current.width * 0.25));
    const minVisibleY = Math.min(60, Math.max(40, sizeRef.current.height * 0.15));
    return {
      left: navbarWidth,
      top: 0,
      right: Math.max(navbarWidth, window.innerWidth - minVisibleX),
      bottom: Math.max(0, window.innerHeight - minVisibleY),
    };
  }, [navbarWidth, size.width, size.height]);

  const modalBody = (
      <div
        ref={modalRef}
        className={`react-modal image-viewer-modal${activeClass}${minimizedClass}${slotClass}`}
        style={{
          ...(!isSlot && hasResized.current ? { width: size.width } : {}),
          ...(!isSlot && hasResized.current && !viewer.isMinimized ? { height: size.height } : {}),
          ...outlineStyle,
          ...(!isSlot ? { zIndex: viewer.zIndex } : {}),
        }}
      >
        {/* Resize handles — window layout only */}
        {!isSlot && !viewer.isMinimized && (
          <>
            <div className="resize-handle resize-handle--n" onMouseDown={(e) => handleResizeMouseDown(e, 'n')} />
            <div className="resize-handle resize-handle--ne" onMouseDown={(e) => handleResizeMouseDown(e, 'ne')} />
            <div className="resize-handle resize-handle--e" onMouseDown={(e) => handleResizeMouseDown(e, 'e')} />
            <div className="resize-handle resize-handle--se" onMouseDown={(e) => handleResizeMouseDown(e, 'se')} />
            <div className="resize-handle resize-handle--s" onMouseDown={(e) => handleResizeMouseDown(e, 's')} />
            <div className="resize-handle resize-handle--sw" onMouseDown={(e) => handleResizeMouseDown(e, 'sw')} />
            <div className="resize-handle resize-handle--w" onMouseDown={(e) => handleResizeMouseDown(e, 'w')} />
            <div className="resize-handle resize-handle--nw" onMouseDown={(e) => handleResizeMouseDown(e, 'nw')} />
          </>
        )}

        {/* Header */}
        <div className="modal-header">
          <div className="modal-title-container">
            <div
              className="modal-title-in-container image-viewer-title"
              title={[viewer.surveyLabel, viewer.layerName].filter(Boolean).join(' — ')}
            >
              <span className="image-viewer-survey-label">{viewer.surveyLabel}</span>
              <span className="image-viewer-layer-label">{viewer.layerName}</span>
            </div>
          </div>
          <div className="modal-header-buttons">
            {!viewer.isMinimized && (
              <>
                <button
                  type="button"
                  className="modal-control image-viewer-help-btn"
                  onClick={handleOpenGuide}
                  title="How to use the Survey Image Viewer"
                  aria-label="How to use the Survey Image Viewer"
                >
                  <MdHelpOutline />
                </button>
                <button
                  type="button"
                  className="modal-control image-viewer-download-btn"
                  onClick={handleDownload}
                  title={activeImageSrc ? 'Download image' : 'Image is not ready to download'}
                  aria-label="Download image"
                  disabled={!activeImageSrc || viewer.hasError || isActiveImagePending}
                >
                  <MdDownload />
                </button>
              </>
            )}
            {canSync && (
              <div
                className={`modal-control image-viewer-sync-btn${viewer.isSynced ? ' image-viewer-sync-btn--active' : ''}`}
                onClick={handleToggleSync}
                title={viewer.isSynced ? 'Disable sync' : 'Enable sync'}
              >
                {viewer.isSynced ? <FiLink2 /> : <FiLink />}
              </div>
            )}
            {canShowFocusToggle && (
              <button
                type="button"
                className={`modal-control image-viewer-focus-btn${isSpatialSyncMode ? ' image-viewer-focus-btn--active' : ''}${isFocusOwner ? ' image-viewer-focus-btn--owner' : ''}`}
                onClick={handleToggleSyncFocus}
                title={focusTooltip}
                aria-label={focusTooltip}
                disabled={focusToggleDisabled}
              >
                <MdCenterFocusStrong />
              </button>
            )}
            {isSlot ? (
              <div
                className="modal-control"
                onClick={handleClose}
                title="Close"
                role="button"
                aria-label="Close"
              >
                <MdRemove />
              </div>
            ) : (
              <>
                <div
                  className="modal-control"
                  onClick={handleMinimize}
                  title={viewer.isMinimized ? 'Restore' : 'Minimize'}
                  role="button"
                  aria-label={viewer.isMinimized ? 'Restore' : 'Minimize'}
                >
                  {viewer.isMinimized ? <TbArrowsMaximize /> : <MdRemove />}
                </div>
                <div className="modal-close" onClick={handleClose}>
                  <MdClose />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="modal-content image-viewer-content">
          {/* Loading spinner — shown until the image is fully rendered */}
          {!isImageReady && !viewer.hasError && !viewer.noImageAvailable && (
            <div className="image-viewer-loading">
              <div className="xa-loader-content">
                <svg
                  aria-hidden="true"
                  className="xa-loader"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              </div>
            </div>
          )}

          {/* Error state */}
          {viewer.hasError && (
            <div className="image-viewer-error">
              <p>{viewer.noImageAvailable ? 'No image available' : 'Image not yet available'}</p>
            </div>
          )}

          {/* Image viewer */}
          {!viewer.hasError && !!activeImageSrc && viewer.imageType === 'STATIC' && (
            <StaticImageViewer
              src={activeImageSrc}
              onLoad={isSlot ? handleImageLoad : handleStaticImageLoad}
              onError={handleImageError}
              shouldFlipHorizontally={shouldFlipStaticImage}
              viewportState={staticViewportState}
              onViewportStateChange={setStaticViewportState}
            />
          )}
          {!viewer.hasError && !!activeImageSrc && viewer.imageType === 'PANO' && (
            <PanoramicImageViewer
              src={activeImageSrc}
              panoramaState={viewer.panoramaState}
              onPositionChange={handlePanoramaPositionChange}
              onZoomChange={handlePanoramaZoomChange}
              onReady={handleImageLoad}
              onError={handleImageError}
            />
          )}
        </div>

        {/* Footer / Navigation */}
        <div className="modal-footer image-viewer-footer">
          {syncGroup?.limitMessage && (
            <div className="image-viewer-sync-limit-msg">
              {syncGroup.limitMessage}
            </div>
          )}
          <div className="image-viewer-nav-buttons">
            <button
              className="xa-button primary image-viewer-nav-btn"
              disabled={!hasPrevious}
              onClick={handleFirst}
              title="First image (Home)"
            >
              <IoPlaySkipBack />
              <span>First</span>
            </button>
            <button
              className="xa-button primary image-viewer-nav-btn"
              disabled={!hasPrevious}
              onClick={handlePrevious}
              title="Previous image (←)"
            >
              <IoChevronBack />
              <span>Previous</span>
            </button>
            <span className="image-viewer-nav-index">
              {viewer.sequentialIndex + 1} / {viewer.layerFeatures.length}
            </span>
            <button
              className="xa-button primary image-viewer-nav-btn"
              disabled={!hasNext}
              onClick={handleNext}
              title="Next image (→)"
            >
              <span>Next</span>
              <IoChevronForward />
            </button>
            <button
              className="xa-button primary image-viewer-nav-btn"
              disabled={!hasNext}
              onClick={handleLast}
              title="Last image (End)"
            >
              <span>Last</span>
              <IoPlaySkipForward />
            </button>
          </div>
          {!hasPrevious && viewer.sequentialIndex === 0 && (
            <div className="image-viewer-no-image-msg">No previous image</div>
          )}
          {!hasNext && viewer.sequentialIndex === viewer.layerFeatures.length - 1 && (
            <div className="image-viewer-no-image-msg">No next image</div>
          )}
        </div>
      </div>
  );

  if (isSlot) {
    return modalBody;
  }

  return (
    <Draggable
      nodeRef={modalRef}
      enableUserSelectHack={false}
      handle=".modal-header"
      position={isDragging ? undefined : dragPosition}
      onStart={handleDragStart}
      onStop={handleDragStop}
      bounds={dragBounds}
    >
      {modalBody}
    </Draggable>
  );
};

export default ImageViewerModal;
